// Service catalog and pricing engine for KRYOTEKS
// Cryo-only offering with price ranges (staff picks exact price in POS).

export const SERVICES = [
  {
    id: 'aesthetic_small',
    name: 'Single Area Cryo',
    subtitle: 'Aesthetics',
    category: 'aesthetic',
    priceMin: 60,
    priceMax: 85,
    description:
      'Targeted cryo aesthetic session for a single facial or body area — tightening, glow, and tone.',
    duration: '10-15 min',
  },
  {
    id: 'ortho_small',
    name: 'Single Area Cryo',
    subtitle: 'Orthopedic — Small',
    category: 'orthopedic',
    priceMin: 40,
    priceMax: 60,
    description:
      'Focused orthopedic cryo on a small joint or muscle group — wrist, elbow, or knee.',
    duration: '5-10 min',
  },
  {
    id: 'ortho_large',
    name: 'Single Area Cryo',
    subtitle: 'Orthopedic — Large',
    category: 'orthopedic',
    priceMin: 60,
    priceMax: 80,
    description:
      'Orthopedic cryo on a larger area — shoulder, hip, or lower-back recovery.',
    duration: '10-15 min',
  },
  {
    id: 'aesthetic_large',
    name: 'Large Area Fat Reduction',
    subtitle: 'Aesthetics',
    category: 'aesthetic',
    priceMin: 120,
    priceMax: 180,
    description:
      'Larger-area cryo aesthetic session focused on cryo-slimming and contouring.',
    duration: '20-30 min',
  },
];

// Package tiers: session count + discount
// 1 (single), 4/6/8 (small packages), 10/12/15/20 (large packages)
export const PACKAGE_TIERS = [
  { sessions: 1, discountPct: 0, label: 'Single Session', size: 'single' },
  { sessions: 4, discountPct: 5, label: '4 Sessions', size: 'small' },
  { sessions: 6, discountPct: 10, label: '6 Sessions', size: 'small' },
  { sessions: 8, discountPct: 15, label: '8 Sessions', size: 'small' },
  { sessions: 10, discountPct: 18, label: '10 Sessions', size: 'large' },
  { sessions: 12, discountPct: 20, label: '12 Sessions', size: 'large' },
  { sessions: 15, discountPct: 25, label: '15 Sessions', size: 'large' },
  { sessions: 20, discountPct: 30, label: '20 Sessions', size: 'large' },
];

// Referral Program config
export const REFERRAL = {
  referralsPerYear: 5,
  commissionPct: 2.5, // lifetime commission
};

export function getService(id) {
  return SERVICES.find((s) => s.id === id) || null;
}

export function getTier(sessions) {
  return (
    PACKAGE_TIERS.find((t) => t.sessions === sessions) || PACKAGE_TIERS[0]
  );
}

/**
 * Clamp a staff-chosen unit price to the service's [priceMin, priceMax] range.
 * If unitPrice is undefined/null, defaults to priceMin.
 */
export function clampPrice(svc, unitPrice) {
  if (!svc) return 0;
  const raw = typeof unitPrice === 'number' && !Number.isNaN(unitPrice)
    ? unitPrice
    : svc.priceMin;
  return Math.min(svc.priceMax, Math.max(svc.priceMin, raw));
}

/**
 * Calculate a cart total with the new pricing model:
 *   items: [{ id, serviceId, sessions, unitPrice }]
 *   unitPrice = staff-chosen price within [priceMin, priceMax].
 * Package tier discount is applied to unitPrice × sessions.
 * (Bundle pricing has been removed with add-ons.)
 */
export function calculateCart(items) {
  const lines = items.map((line) => {
    const svc = getService(line.serviceId);
    if (!svc) {
      return null;
    }
    const tier = getTier(line.sessions);
    const unitPrice = clampPrice(svc, line.unitPrice);
    const sessions = line.sessions;
    const lineStandardSubtotal = unitPrice * sessions;
    const lineDiscount = lineStandardSubtotal * (tier.discountPct / 100);
    const lineTotal = lineStandardSubtotal - lineDiscount;
    return {
      ...line,
      service: svc,
      unitPrice,
      sessions,
      tier,
      lineStandardSubtotal,
      lineDiscount,
      lineTotal,
    };
  }).filter(Boolean);

  const subtotal = lines.reduce((s, l) => s + l.lineStandardSubtotal, 0);
  const packageDiscount = lines.reduce((s, l) => s + l.lineDiscount, 0);
  const total = lines.reduce((s, l) => s + l.lineTotal, 0);

  return {
    lines,
    subtotal,
    packageDiscount,
    total,
    bundleSavings: 0, // kept for backward compat with historical sales
  };
}

export const money = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Math.max(0, n));

export const moneyRange = (svc) =>
  svc && svc.priceMin != null && svc.priceMax != null
    ? `${money(svc.priceMin)} – ${money(svc.priceMax)}`
    : '';
